import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  InputAdornment,
  makeStyles,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core"
import { Alert, Autocomplete, Color } from "@material-ui/lab"
import React from "react"
import {
  NudgeFragment,
  useGetAllActiveNudgesQuery,
  useInsertNudgeMutation,
  useUpdateNudgeMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useInAppResources } from "../../hooks/useInAppResources"
import { useIsOrgAdmin } from "../../hooks/useIsOrgAdmin"
import { useTechniquesAndInterventions } from "../../hooks/useTechniquesAndInterventions"
import { useUser } from "../../hooks/useUser"
import { NudgeDomain, nudgeOrder } from "../../utils/constants"
import { dummyResource, isNudge } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { InAppResourcesTableData } from "../../utils/types"
import Button, { GhostButton } from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import { Tabs } from "../molecules/Tabs"

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: theme.spacing(2),
  },
  title: {
    padding: theme.spacing(4, 4, 2),
  },
  contentRoot: {
    padding: theme.spacing(1, 4, 4),
  },
  tabButton: {
    backgroundColor: colors.primary.ice_03,
    opacity: 1,
    color: colors.primary.midnight,
    textTransform: "capitalize",
    minHeight: 50,
    "&.Mui-selected": {
      backgroundColor: colors.primary.ice,
    },
    "&:first-child": {
      borderTopLeftRadius: 100,
      borderBottomLeftRadius: 100,
    },
    "&:last-child": {
      borderTopRightRadius: 100,
      borderBottomRightRadius: 100,
    },
    "&.MuiTab-root": {
      minWidth: "unset",
    },
  },
  tabPanel: {
    display: "grid",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
  },
  domainLabel: {
    display: "flex",
    justifyContent: "center",
  },
  domainDot: {
    height: 10,
    width: 10,
    margin: 8,
    borderRadius: 20,
    display: "inline-block",
  },
  cancelContainer: {
    marginTop: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pickerContainer: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 12,
    padding: theme.spacing(5, 3, 4),
    position: "relative",
    margin: theme.spacing(3, 3, 0, 3),
    display: "grid",
    gap: theme.spacing(4),
  },
  pickerTabsContainer: {
    // width offset for pickerContainer margin
    width: `calc(100% - ${theme.spacing(6)}px)`,
    position: "absolute",
    left: theme.spacing(3),
    top: -30,
    border: `4px solid ${colors.neutral.white}`,
    borderRadius: 150,
    backgroundColor: colors.neutral.white,
  },
  pickerBody: {
    display: "grid",
    gap: theme.spacing(4),
  },
  dayPicker: {
    paddingTop: theme.spacing(2),
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
  },
  dayCheckbox: {
    borderColor: colors.neutral.clay,
    borderWidth: 1,
    borderStyle: "solid",
    borderLeftWidth: 0,
    borderRadius: 0,
    "&:first-child": {
      borderLeftWidth: 1,
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },
    "&:last-child": {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
    },

    "&:hover": {
      backgroundColor: colors.primary.ice_03,
    },
    "&.Mui-checked": {
      backgroundColor: colors.primary.ice,
    },
    "&.Mui-checked:hover": {
      backgroundColor: colors.primary.periwinkle,
    },
    "&:active": {
      backgroundColor: colors.primary.periwinkle,
    },
    "&.Mui-focusVisible": {
      border: `2px solid ${colors.primary.periwinkle}`,
    },
  },
  dayCheckboxLabel: {
    color: colors.primary.shadow,
    fontWeight: "bold",
  },
  focusVisible: {
    "&.Mui-focusVisible": {
      boxShadow: `0 0 0 2px ${colors.primary.periwinkle}`,
    },
  },
}))

enum ScheduleTab {
  NUDGE = "Nudge",
}

type ItemFragment = NudgeFragment
interface ScheduleDialogProps extends DialogProps {
  item: ItemFragment | undefined
}

export const TechniqueAndInterventionDialog: React.FC<ScheduleDialogProps> = ({
  item,
  ...props
}) => {
  const newTechnique = item ? false : true
  const classes = useStyles()
  const { user } = useUser()
  const { inAppResources } = useInAppResources(user.organizations?.[0].id ?? -1)
  const graphqlClient = useGraphQLClient()
  const { data: nudges } = useGetAllActiveNudgesQuery(graphqlClient)
  const [scheduleTab, setScheduleTab] = React.useState(ScheduleTab.NUDGE)
  const [domain, setDomain] = React.useState<NudgeDomain | null>(null)
  const [message, setMessage] = React.useState("‎")
  const [inAppResource, setInAppResource] =
    React.useState<InAppResourcesTableData>(dummyResource)
  const [technique, setTechnique] = React.useState<NudgeFragment | null>(null)
  const [typedTechnique, setTypedTechnique] = React.useState<
    string | undefined
  >(" .")
  const { refetchOrgTechniques, refetchAllTechniques } =
    useTechniquesAndInterventions()
  const onSuccess = () => {
    refetchAllTechniques()
    refetchOrgTechniques()
  }
  const { mutate: updateNudgeMutation } = useUpdateNudgeMutation(
    graphqlClient,
    { onSuccess }
  )
  const { mutate: insertNudgeMutation } = useInsertNudgeMutation(
    graphqlClient,
    { onSuccess }
  )
  const isOrgAdmin = useIsOrgAdmin()
  const isKsana = isOrgAdmin && user?.email?.endsWith("@ksanahealth.com")
  const getDomains = (): NudgeDomain[] => {
    const domains = Array.from(new Set(nudges?.nudges.map((n) => n.domain)))
    const intersectionDomains = nudgeOrder.filter((n) => domains.includes(n))
    return intersectionDomains
  }

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  const save = async () => {
    try {
      if (!user) {
        throw new Error("User not set")
      }

      if (scheduleTab === ScheduleTab.NUDGE) {
        if (!domain) {
          throw new Error("No domain chosen")
        }
        if (!typedTechnique) {
          throw new Error("No technique chosen")
        }
        // TODO: determine if we can remove the invisible character
        if (message === "" || message === "‎") {
          throw new Error("Include a message to continue")
        }

        if (!item) {
          item = {
            id: 0,
            created_at: 0,
            created_by: user.id,
            active: true,
            owner_org_id: user.organizations?.[0].id,
            public: false,
            domain: domain,
            technique: typedTechnique,
            default_messaging: message,
            resource_id: inAppResource?.id.data ?? null,
          }
        }

        if (item && isNudge(item)) {
          if ((item.created_by === user.id || isKsana) && !newTechnique) {
            updateNudgeMutation({
              id: item.id,
              default_messaging: message,
              resource: inAppResource?.id.data,
              created_by: item.created_by,
              technique: typedTechnique,
              domain: domain,
            })
          } else {
            insertNudgeMutation({
              created_by: isKsana ? null : user.id,
              owner_org_id: user?.organizations?.[0].id,
              default_messaging: message,
              domain: domain,
              public: isKsana ? true : false,
              technique: typedTechnique,
              resource: inAppResource?.id.data,
            })
          }
        }
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: newTechnique ? "Added!" : "Updated!",
      })

      // in order to fire the onClose, it needs garbage arguments
      props.onClose?.({}, "backdropClick")
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: (error as unknown as { message: string }).message,
      })
      console.error("schedule error", error)
    }
  }
  // pre-fill with item
  React.useEffect(() => {
    if (!item) {
      setInAppResource(dummyResource)
      return
    }

    const domain = item?.domain
    setDomain(nudgeOrder.find((n) => n === domain) ?? null)
    setTechnique(item)
    setMessage(item.default_messaging)
    const resources = inAppResources
    const theResouce =
      resources.filter((x) => x.id.data === item?.resource_id) ?? dummyResource
    setInAppResource(theResouce[0])
  }, [item, inAppResources])

  React.useEffect(() => {
    setTypedTechnique(technique?.technique)
  }, [technique])

  React.useEffect(() => {
    if (!props.open) {
      // clear state on close
      // TODO: use initial tab
      setScheduleTab(ScheduleTab.NUDGE)
      setDomain(null)
      setMessage("‎")
      setTechnique(null)
      setTypedTechnique(undefined)

      const emptyRsource = inAppResources.filter((x) => x.id.data === -1)[0]
      setInAppResource(emptyRsource)
    }
  }, [props.open, inAppResources])

  return (
    <>
      <Dialog {...props} fullWidth maxWidth="xs">
        {/* Sort of a hack, but I have to give the `onClose` arguments to work :shrug: */}
        <IconButton
          className={classes.closeButton}
          onClick={(e) => {
            props.onClose?.(e, "backdropClick")
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle disableTypography className={classes.title}>
          <Typography variant="h3" component="h2">
            {newTechnique ? "Add" : "Edit"} Technique
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.contentRoot}>
          <Tabs
            ariaLabel="Nudges tab"
            tabs={[{ label: ScheduleTab.NUDGE, className: classes.tabButton }]}
            enabled={newTechnique}
            onChange={(tab) => {
              setScheduleTab(tab)
            }}
            initialTab={scheduleTab}
          >
            {/* Nudge Tab */}
            <div className={classes.tabPanel}>
              <Autocomplete
                id="domain-name"
                options={getDomains()}
                value={domain}
                onChange={(_, value) => {
                  if (value !== domain) {
                    setTechnique(null)
                  }
                  setDomain(value)
                }}
                autoSelect
                autoComplete
                autoHighlight
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Domain Name"
                    variant="outlined"
                    required
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start" style={{ margin: 0 }}>
                          <div
                            className={classes.domainDot}
                            style={{
                              backgroundColor: domain
                                ? colors.schedule[domain].border
                                : "",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(domainOption) => (
                  <span className={classes.domainLabel}>
                    <div
                      className={classes.domainDot}
                      style={{
                        backgroundColor: domainOption
                          ? colors.schedule[domainOption].border
                          : "",
                      }}
                    />
                    {domainOption}
                  </span>
                )}
              />
              <TextField
                value={typedTechnique ?? " "}
                onChange={(e) => setTypedTechnique(e.target.value)}
                label="Technique"
                variant="outlined"
                fullWidth
                required
              />
              {/* {scheduler} */}
              <Autocomplete
                id="inAppResource"
                options={inAppResources}
                getOptionLabel={(option) => option.title.data}
                getOptionSelected={(option, value) =>
                  option.id.data === value.id.data
                }
                value={inAppResource}
                onChange={(_, value) => {
                  setInAppResource(value ?? dummyResource)
                }}
                autoSelect
                autoHighlight
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Resource link"
                    variant="outlined"
                  />
                )}
              />
              <TextField
                id="outlined-multiline-static"
                aria-label="Content TextArea"
                label="Message"
                multiline
                variant="outlined"
                rows={4}
                required
                style={{ width: "100%" }}
                value={message ?? "‎"}
                onChange={(e) => setMessage(e.target.value)}
                disabled={item?.owner_org_id === null}
                defaultValue="Message"
              />
            </div>
          </Tabs>
          <Button onClick={save} customVariant="primary" fullWidth>
            Save
          </Button>
          <div className={classes.cancelContainer}>
            <GhostButton
              onClick={(e) => {
                props.onClose?.(e, "backdropClick")
              }}
            >
              Cancel
            </GhostButton>
          </div>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
